import { collidesWithRegion } from './collision';
import { boundsOfShape, pointInPolygon, pointInRect } from './geometry';
import type { Point, Rect } from './geometry';
import type { RegionDefinition, RegionZone, RegionZoneKind } from './region-definition';
import { hashCode, mulberry32 } from './rng';

export type PlacementResult =
  | { ok: true; point: Point; zoneId: string }
  | { ok: false; reason: 'missing-zone' | 'no-valid-point' };

export interface PlacementInput {
  region: RegionDefinition;
  zoneKinds: readonly RegionZoneKind[];
  seed: string;
  radius: number;
  occupied: readonly Point[];
  minSeparation: number;
  avoidEntrancesBy: number;
  maxAttempts?: number;
}

function nonNegativeFinite(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function normalizedAttempts(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) return 32;
  return Math.max(1, Math.floor(value));
}

function pointMatchesZone(point: Point, zone: RegionZone): boolean {
  return 'points' in zone.area ? pointInPolygon(point, zone.area) : pointInRect(point, zone.area);
}

export function pointMatchesAnyZone(
  point: Point,
  kinds: readonly RegionZoneKind[],
  zones: readonly RegionZone[],
): boolean {
  return zones.some((zone) => kinds.includes(zone.kind) && pointMatchesZone(point, zone));
}

function squaredDistanceToRect(point: Point, rect: Rect): number {
  const closestX = Math.max(rect.x, Math.min(point.x, rect.x + rect.w));
  const closestY = Math.max(rect.y, Math.min(point.y, rect.y + rect.h));
  const dx = point.x - closestX;
  const dy = point.y - closestY;
  return dx * dx + dy * dy;
}

function normalized(input: PlacementInput): {
  radius: number;
  minSeparation: number;
  avoidEntrancesBy: number;
} {
  return {
    radius: nonNegativeFinite(input.radius),
    minSeparation: nonNegativeFinite(input.minSeparation),
    avoidEntrancesBy: nonNegativeFinite(input.avoidEntrancesBy),
  };
}

export function isPlacementValid(input: PlacementInput, point: Point): boolean {
  if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return false;

  const { radius, minSeparation, avoidEntrancesBy } = normalized(input);
  const { bounds } = input.region;
  if (
    point.x - radius < bounds.x ||
    point.x + radius > bounds.x + bounds.w ||
    point.y - radius < bounds.y ||
    point.y + radius > bounds.y + bounds.h
  ) {
    return false;
  }

  if (!pointMatchesAnyZone(point, input.zoneKinds, input.region.zones)) return false;
  if (collidesWithRegion(input.region, point, radius)) return false;
  if (pointMatchesAnyZone(point, ['no-spawn'], input.region.zones)) return false;

  const entranceClearance = avoidEntrancesBy + radius;
  const entranceClearanceSquared = entranceClearance * entranceClearance;
  if (
    input.region.entrances.some(
      (entrance) => squaredDistanceToRect(point, entrance.area) < entranceClearanceSquared,
    )
  ) {
    return false;
  }

  const occupiedClearance = minSeparation + radius;
  const occupiedClearanceSquared = occupiedClearance * occupiedClearance;
  return input.occupied.every((occupied) => {
    if (!Number.isFinite(occupied.x) || !Number.isFinite(occupied.y)) return true;
    const dx = point.x - occupied.x;
    const dy = point.y - occupied.y;
    return dx * dx + dy * dy >= occupiedClearanceSquared;
  });
}

export function placeInRegion(input: PlacementInput): PlacementResult {
  const candidateZones = input.region.zones.filter((zone) => input.zoneKinds.includes(zone.kind));
  if (candidateZones.length === 0) return { ok: false, reason: 'missing-zone' };

  const random = mulberry32(hashCode(input.seed));
  const attempts = normalizedAttempts(input.maxAttempts);

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const zone = candidateZones[Math.floor(random() * candidateZones.length)];
    if (!zone) continue;

    const bounds = boundsOfShape(zone.area);
    const point = {
      x: bounds.x + random() * bounds.w,
      y: bounds.y + random() * bounds.h,
    };
    if (!pointMatchesZone(point, zone)) continue;
    if (isPlacementValid(input, point)) return { ok: true, point, zoneId: zone.id };
  }

  return { ok: false, reason: 'no-valid-point' };
}
